/**
 * File: src/layout/CandidateLayout/index.js
 * Layer: Layout
 * Description: Wraps candidate-facing pages with the shared portal header,
 * breadcrumbs, drawer state, and session-related dialogs.
 */
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

import Header from './Header';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import SetPasswordDialog from 'views/pages/authentication/auth-forms/SetPasswordDialog';

import navigation from 'menu-items';
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';

import { IconChevronRight } from '@tabler/icons-react';
import useAuth from 'hooks/useAuth';
import SessionTimeoutPassword from 'ui-component/modals/SessionTimeoutPassword';
import BackDropLoader from 'ui-component/BackDropLoader';
import menuItems from 'menu-items';

const Main = styled('main', { shouldForwardProp: (prop) => !['open', 'layout'].includes(prop) })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    minHeight: 0,
    width: 'calc(100% - 40px)',
    marginTop: theme.spacing(0.5),
    marginLeft: '20px',
    marginRight: '20px',
    paddingTop: '0',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create('margin', {
        easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter + 200
    }),
    [theme.breakpoints.down('md')]: {
        padding: '16px'
    },
    [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 20px)',
        marginLeft: '10px',
        marginRight: '10px',
        padding: '16px',
        marginTop: theme.spacing(2)
    }
}));

/**
 * Candidate portal shell used by authenticated candidate routes.
 * @returns {JSX.Element}
 */
const CandidateLayout = () => {
    const { isSessionTimeout } = useAuth();
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { drawerType, container, layout } = useConfig();
    const location = useLocation();
    const currentPath = location.pathname;
    const { clientSettings } = useSelector((state) => state.settings);
    const { trial = false } = clientSettings;

    useLayoutEffect(() => {
        // Redirect trial users away from routes that are disabled for their plan
        // even when they navigate directly to the URL.
        const { items = [] } = menuItems;
        const item = items.find((i) => i.url === currentPath);
        if (trial && item) {
            const { permissions } = item;
            if (permissions?.disableInTrial) {
                window.location.assign('/');
            }
        }
    }, [currentPath, trial]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
    }, [dispatch, drawerType]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
    }, [dispatch, drawerType]);

    useEffect(() => {
        if (matchDownMd) {
            dispatch(openDrawer(true));
        }
    }, [dispatch, matchDownMd]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    const header = useMemo(
        () => (
            <Toolbar
                sx={{
                    p: condition ? '10px' : '16px',
                    px: { xs: 1.5, sm: 2 },
                    minWidth: 0,
                    bgcolor: theme.palette.mode !== 'dark' ? theme.palette.secondary.main : theme.palette.background.default
                }}
            >
                <Header />
            </Toolbar>
        ),
        [condition, theme.palette.background.default, theme.palette.mode, theme.palette.secondary.main]
    );

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                flexDirection: 'column',
                bgcolor: theme.palette.mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[100]
            }}
        >
            <CssBaseline />

            <AppBar enableColorOnDark position="sticky" color="inherit" elevation={0} sx={{ bgcolor: theme.palette.background.default }}>
                {header}
            </AppBar>

            <Main open={drawerOpen}>
                <Container maxWidth={container ? 'lg' : false} {...(!container && { sx: { px: { xs: 0 } } })}>
                    <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    <Outlet />
                </Container>
            </Main>

            <SessionTimeoutPassword toggleDialog={isSessionTimeout} />
            <SetPasswordDialog />
            <BackDropLoader />
        </Box>
    );
};

export default CandidateLayout;
